import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Button, Theme, useTheme } from "@mui/material";
import Link from "next/link";

import { Props, shareOnTwitter } from "~/components/quest/page/[id]/lib";
import { copyToClipboard } from "~/lib/client/browser_ops";
import {
  fixedWidth,
  flexRow,
  mediaQueryIsDesktop,
  mediaQueryIsMobile,
} from "~/lib/client/design";
import { engFont } from "~/lib/client/fonts";

type Quest = Props["quest"];

export const CompletedModalContent = ({
  category,
  quest,
}: {
  category: Props["quest"]["category"];
  quest: Quest;
}) => {
  const theme = useTheme();

  // 検定に紐づくクエストかどうかを判定
  const hasExams = quest.externalExams && quest.externalExams.length > 0;

  // 受験期間の表示用フォーマット関数
  const formatExamPeriod = (examAvailableTo: string | null | undefined) => {
    if (!examAvailableTo) return "受験期間内に";

    try {
      const endDate = new Date(examAvailableTo);
      const month = endDate.getMonth() + 1;
      const day = endDate.getDate();
      const hours = endDate.getHours();
      const minutes = endDate.getMinutes();

      return `受験期間（${month}月${day}日${hours}:${minutes.toString().padStart(2, "0")}まで）内に`;
    } catch {
      return "受験期間内に";
    }
  };

  // 最初の検定の受験期間を取得（複数ある場合は最初のもの）
  const examPeriodText =
    hasExams && quest.externalExams?.[0]
      ? formatExamPeriod((quest.externalExams[0] as any).examAvailableTo)
      : "受験期間内に";

  return (
    <div css={[completedModalContentRootStyle, hasExams && examModeStyle]}>
      <p css={thankYouStyle(theme)} className={engFont.className}>
        {hasExams ? "レシートの提出が完了しました！" : "QUEST COMPLETED!"}
      </p>
      <div>
        {hasExams ? (
          <p>
            下記URLから{examPeriodText}受験してください。
            <br />
            受験期間中は何度でも受験することができます。
          </p>
        ) : (
          <p>
            リワードは後ほど送られ、サービス内で通知されます。
            <br />
            引き続きFUSSYをお楽しみください。
          </p>
        )}
        {!hasExams && (
          <CenterContainer>
            <Link href={`/category/${category.id}`}>
              「{category.name}」の他のクエストを見つける
            </Link>
            <Link href={`/category/${category.id}?tab=DATABASE`}>
              「{category.name}」のデータベースを見る
            </Link>
            <Link href={`/me`}>これまでに実行したクエストを見る</Link>
          </CenterContainer>
        )}
      </div>

      {quest.externalExams && quest.externalExams.length > 0 && (
        <section css={examSectionStyle}>
          <ul>
            {quest.externalExams.map((exam) => (
              <li key={exam.id}>
                <Link
                  href={`/category/${category.id}/exam/${(exam as any).internalId}`}
                >
                  {exam.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!hasExams &&
        quest.questTasks.map((t) => t.form).filter(Boolean).length > 0 && (
          <section>
            <p>他の人の投稿はこちらから楽しめます。</p>
            <ul>
              {quest.questTasks
                .map((t) => t.form)
                .compact()
                .map((form) => (
                  <li key={form.id}>
                    <Link href={`/database/${form.id}`}>{form.title}</Link>
                  </li>
                ))}
            </ul>
            <p css={notationStyle}>
              投稿を修正したい場合は、自分の投稿をクリックまたはタップすると修正できます。
            </p>
          </section>
        )}

      {!hasExams && (
        <div>
          <p css={leadShareStyle}>このクエストをシェアしてみんなで楽しもう！</p>
          <CenterContainer>
            <Button
              variant={"outlined"}
              onClick={() =>
                copyToClipboard(`https://www.fussy.fun/quest/${quest.id}`)
              }
            >
              シェアする
            </Button>
            <Button variant={"outlined"} onClick={() => shareOnTwitter(quest)}>
              Xにポストする
            </Button>
          </CenterContainer>
        </div>
      )}
    </div>
  );
};

const completedModalContentRootStyle = css`
  ${mediaQueryIsMobile(css`
    width: 95vw;
    padding-bottom: 1rem;
  `)};

  ${mediaQueryIsDesktop(css`
    ${fixedWidth("40rem")}
  `)};

  padding: 1rem;

  a {
    text-decoration: underline;
    color: #666;
  }
`;

const thankYouStyle = (theme: Theme) => css`
  ${flexRow};
  justify-content: center;
  font-size: 36px;
  color: ${theme.palette.primary.main};
  margin: 0;
`;

const leadShareStyle = css`
  margin-top: 5rem;
`;

const notationStyle = css`
  color: #666;
  font-size: 0.8rem;
`;

const examModeStyle = css`
  padding: 1.5rem;
  min-height: auto;

  ${mediaQueryIsMobile(css`
    width: 85vw;
    padding: 1rem;
  `)};

  ${mediaQueryIsDesktop(css`
    width: 30rem;
    max-width: 30rem;
  `)};
`;

const examSectionStyle = css`
  margin-top: 1rem;

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    margin-bottom: 0.25rem;
  }
`;

const CenterContainer = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  gap: 0.25rem;
`;
